import { useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { PencilIcon, SearchIcon, XIcon } from "lucide-react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { useUserSheet } from "@/components/user-sheet/userSheetContext";

import { Column, EmptyState, Row, Text } from "@/components";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Textarea } from "@/components/ui/textarea";

import { useParseScreeningQuestionsAndAnswers } from "@/hooks/mutations/useParseScreeningQuestionsAndAnswers";
import { useSetCandidacyInfo } from "@/hooks/sets/useSetCandidacyInfo";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { Candidacy } from "@/types";

export const ScreeningQuestionWithAnswer = ({
  answer,
  question,
}: {
  answer: string;
  question: string;
}) => {
  return (
    <Column className="gap-1 border-b border-slate-100 py-4 first:pt-0 last:border-0 last:pb-0">
      <Text className="text-sm font-normal text-[#121212] dark:text-[#FAFAFA]">
        {question}
      </Text>
      <Text className="text-sm font-medium text-[#121212] dark:text-[#FAFAFA]">
        {answer}
      </Text>
    </Column>
  );
};

export const ScreeningQuestionWithoutAnswer = ({
  question,
}: {
  question: string;
}) => {
  return (
    <Column className="border-b border-slate-100 py-4 opacity-50 first:pt-0 last:border-0 last:pb-0">
      <Row className="items-center justify-between gap-4">
        <Text className="text-sm font-normal text-[#121212] dark:text-[#FAFAFA]">
          {question}
        </Text>
        <Button className="shrink-0" disabled size="icon" variant="ghost">
          <XIcon className="h-4 w-4 text-slate-500" />
        </Button>
      </Row>
    </Column>
  );
};

const parseScreeningQuestionsAndAnswersSchema = z.object({
  screeningQuestionsAndAnswers: z.string().min(1, "Required"),
});

const ParseScreeningQuestionsAndAnswersDialog = ({
  candidacyId,
  open,
  onOpenChange,
}: {
  candidacyId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const { mutate: parseScreeningQuestionsAndAnswers, isPending } =
    useParseScreeningQuestionsAndAnswers();
  const setCandidacyInfo = useSetCandidacyInfo();

  const form = useForm<z.infer<typeof parseScreeningQuestionsAndAnswersSchema>>(
    {
      defaultValues: { screeningQuestionsAndAnswers: "" },
      resolver: zodResolver(parseScreeningQuestionsAndAnswersSchema),
    },
  );

  const handleSubmit = (
    data: z.infer<typeof parseScreeningQuestionsAndAnswersSchema>,
  ) => {
    mixpanelUtils.trackFormSubmission(
      "Parse screening QA",
      MIXPANEL_SCREENS.CANDIDATE_SHEET,
    );
    mixpanelUtils.trackButtonClick(
      "Import Screening Questions and Answers Submit",
      MIXPANEL_SCREENS.CANDIDATE_SHEET,
    );
    parseScreeningQuestionsAndAnswers(
      {
        candidacyId,
        parseScreeningQuestionsAndAnswersRequest: {
          inputText: data.screeningQuestionsAndAnswers,
        },
      },
      {
        onSuccess: (candidacy) => {
          form.reset();
          setCandidacyInfo({
            candidacyId,
            screeningAnswers: candidacy.screeningAnswers,
          });
          onOpenChange(false);
        },
      },
    );
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(open) => {
        // Reset the form when the dialog is closed.
        form.reset();
        onOpenChange(open);
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="text-lg text-slate-700">
            Import screening questions from text
          </DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form
            className="flex flex-col gap-4"
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onSubmit={form.handleSubmit(handleSubmit)}
          >
            <FormField
              control={form.control}
              name="screeningQuestionsAndAnswers"
              render={({ field }) => (
                <FormItem className="text-slate-700">
                  <FormLabel>Screening Questions</FormLabel>
                  <FormControl>
                    <Textarea {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Row className="justify-end">
              <Button
                className="rounded-full"
                disabled={isPending}
                type="submit"
              >
                Import
              </Button>
            </Row>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export const ScreeningAnswers = ({
  candidacyId,
  screeningAnswers,
}: {
  candidacyId: string;
  screeningAnswers: Candidacy["screeningAnswers"];
}) => {
  /* Context */
  const { isReadOnly } = useUserSheet();

  /* React Hooks */
  const [
    isParseScreeningQuestionsAndAnswersDialogOpen,
    setIsParseScreeningQuestionsAndAnswersDialogOpen,
  ] = useState(false);

  /* Helpers */
  const hasScreeningAnswers =
    (screeningAnswers?.screeningAnswers.length ?? 0) > 0;

  /* Render */
  const render = () => {
    if (!hasScreeningAnswers) {
      if (isReadOnly) {
        return (
          <EmptyState
            description="This candidate didn't answer any application questions."
            descriptionClassName="text-xs"
            headerClassName="text-sm"
            Icon={SearchIcon}
            isIconHidden
            title="No screening questions yet"
          />
        );
      }

      return (
        <>
          <EmptyState
            description="Import screening questions from text."
            descriptionClassName="text-xs"
            handlePrimaryClick={() => {
              setIsParseScreeningQuestionsAndAnswersDialogOpen(true);
            }}
            headerClassName="text-sm"
            Icon={SearchIcon}
            isIconHidden
            PrimaryButtonIcon={PencilIcon}
            primaryButtonClassName="rounded-md"
            primaryButtonName="Import from text"
            primaryButtonVariant="secondary"
            title="No screening questions yet"
          />
          <ParseScreeningQuestionsAndAnswersDialog
            candidacyId={candidacyId}
            onOpenChange={setIsParseScreeningQuestionsAndAnswersDialogOpen}
            open={isParseScreeningQuestionsAndAnswersDialogOpen}
          />
        </>
      );
    }

    return (
      <Column className="gap-0">
        {(screeningAnswers?.screeningAnswers ?? []).map(
          (screeningAnswer, index) => {
            const questionWithNumber = `${(index + 1).toString()}. ${screeningAnswer.question}`;

            if (screeningAnswer.answer) {
              return (
                <ScreeningQuestionWithAnswer
                  answer={screeningAnswer.answer}
                  key={index}
                  question={questionWithNumber}
                />
              );
            }
            return (
              <ScreeningQuestionWithoutAnswer
                key={index}
                question={questionWithNumber}
              />
            );
          },
        )}
      </Column>
    );
  };

  return render();
};
